import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { luxuryHireApi, LuxuryBooking } from '../../../services/luxuryHireApi';

const formatCurrency = (amount: number, currency: string = 'NGN') => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
  }).format(amount);
};

const BookingConfirmation: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<LuxuryBooking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<'paystack' | 'seerbit'>('paystack');

  useEffect(() => {
    if (bookingId) {
      fetchBooking(parseInt(bookingId));
    }
  }, [bookingId]);

  const fetchBooking = async (id: number) => {
    try {
      setLoading(true);
      const data = await luxuryHireApi.getBooking(id);
      setBooking(data);
    } catch (err: any) {
      setError('Failed to load booking details.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (paymentType: 'deposit' | 'balance' | 'full') => {
    if (!booking) return;
    
    try {
      setPaymentLoading(true);
      setError(null);
      
      const result = await luxuryHireApi.initiatePayment(booking.id, paymentType, selectedGateway);
      
      if (result.success && result.payment_url) {
        window.location.href = result.payment_url;
      } else {
        setError('Failed to initialize payment. Please try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Payment initialization failed. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-amber-500/30 rounded-full animate-spin border-t-amber-500"></div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white">
        <p className="text-slate-400 text-lg mb-4">{error || 'Booking not found'}</p>
        <button onClick={() => navigate('/luxury-hire')} className="px-6 py-2 bg-amber-500 text-white rounded-full">
          Back to Catalog
        </button>
      </div>
    );
  }

  if (!booking) return null;

  const isPending = booking.payment_status === 'pending';
  const isDepositPaid = booking.payment_status === 'deposit_paid';
  const isFullyPaid = booking.payment_status === 'fully_paid';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className={`w-20 h-20 ${isFullyPaid ? 'bg-green-500/20' : 'bg-amber-500/20'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {isFullyPaid ? (
              <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-10 h-10 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {isFullyPaid ? 'Booking Confirmed!' : 'Complete Your Booking'}
          </h1>
          <p className="text-slate-400">
            {isFullyPaid 
              ? 'Your luxury vehicle booking is confirmed.' 
              : isPending 
                ? 'Pay the deposit to confirm your booking.'
                : 'Pay the remaining balance to complete your booking.'
            }
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400 text-center">
            {error}
          </div>
        )}

        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
          <div className="bg-amber-500/10 p-4 border-b border-slate-700">
            <p className="text-amber-400 text-sm font-medium">Booking Reference</p>
            <p className="text-white text-2xl font-bold tracking-wide">{booking.booking_reference}</p>
          </div>

          <div className="p-6 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-20 h-16 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0 flex items-center justify-center text-3xl">
                🚗
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">{booking.vehicle_name}</h3>
                <p className="text-slate-400 text-sm">{booking.vehicle_category}</p>
              </div>
            </div>

            <div className="border-t border-slate-700 pt-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-400">Date</span>
                <span className="text-white">
                  {new Date(booking.start_datetime).toLocaleDateString('en-NG', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Time</span>
                <span className="text-white">
                  {new Date(booking.start_datetime).toLocaleTimeString('en-NG', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Duration</span>
                <span className="text-white">{booking.duration_hours.toFixed(1)} hours</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Pickup</span>
                <span className="text-white text-right max-w-xs">{booking.pickup_address}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Payment Status</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  isFullyPaid ? 'bg-green-500/20 text-green-400' :
                  isDepositPaid ? 'bg-blue-500/20 text-blue-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {isFullyPaid ? 'Fully Paid' : isDepositPaid ? 'Deposit Paid' : 'Pending'}
                </span>
              </div>
            </div>

            <div className="border-t border-slate-700 pt-4">
              <div className="flex justify-between text-lg mb-3">
                <span className="text-white font-medium">Total Amount</span>
                <span className="text-amber-400 font-bold">{formatCurrency(booking.total_amount, booking.currency)}</span>
              </div>
              
              <div className="bg-slate-700/50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Deposit ({Math.round((booking.deposit_amount / booking.total_amount) * 100)}%)</span>
                  <span className={`font-semibold ${isDepositPaid || isFullyPaid ? 'text-green-400' : 'text-amber-400'}`}>
                    {formatCurrency(booking.deposit_amount, booking.currency)}
                    {(isDepositPaid || isFullyPaid) && ' ✓'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Balance Due</span>
                  <span className={`font-semibold ${isFullyPaid ? 'text-green-400' : 'text-white'}`}>
                    {formatCurrency(booking.balance_amount, booking.currency)}
                    {isFullyPaid && ' ✓'}
                  </span>
                </div>
              </div>
            </div>

            {!isFullyPaid && (
              <div className="border-t border-slate-700 pt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Payment Gateway</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedGateway('paystack')}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                        selectedGateway === 'paystack'
                          ? 'border-amber-500 bg-amber-500/10 text-white'
                          : 'border-slate-600 bg-slate-700/50 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      <span className="font-medium">Paystack</span>
                      <p className="text-xs mt-1 opacity-70">Card, Bank, USSD</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedGateway('seerbit')}
                      className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                        selectedGateway === 'seerbit'
                          ? 'border-amber-500 bg-amber-500/10 text-white'
                          : 'border-slate-600 bg-slate-700/50 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      <span className="font-medium">SeerBit</span>
                      <p className="text-xs mt-1 opacity-70">Card, Transfer</p>
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {isPending && (
                    <>
                      <button
                        onClick={() => handlePayment('deposit')}
                        disabled={paymentLoading}
                        className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold text-lg hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {paymentLoading ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          <>Pay Deposit - {formatCurrency(booking.deposit_amount, booking.currency)}</>
                        )}
                      </button>
                      <button
                        onClick={() => handlePayment('full')}
                        disabled={paymentLoading}
                        className="w-full py-3 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-600 transition-colors disabled:opacity-50"
                      >
                        Pay Full Amount - {formatCurrency(booking.total_amount, booking.currency)}
                      </button>
                    </>
                  )}
                  
                  {isDepositPaid && (
                    <button
                      onClick={() => handlePayment('balance')}
                      disabled={paymentLoading}
                      className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold text-lg hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {paymentLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        <>Pay Balance - {formatCurrency(booking.balance_amount, booking.currency)}</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {isFullyPaid && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <p className="text-green-400 text-sm">
                  <strong>Booking Confirmed!</strong> Your luxury vehicle is reserved. Our team will contact you with driver details and pickup confirmation.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-4 mt-8">
          <button
            onClick={() => navigate('/customer-dashboard')}
            className="flex-1 py-3 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-600 transition-colors"
          >
            Go to Dashboard
          </button>
          <button
            onClick={() => navigate('/luxury-hire')}
            className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:from-amber-600 hover:to-orange-600 transition-colors"
          >
            Browse More Vehicles
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
